import type { ConsumerApiClient } from '../client';

export interface Product {
  id: string;
  name: string;
  type: 'photo' | 'letter' | 'card' | 'document' | 'photobook';
  description: string;
  basePriceCents: number;
  imageUrl?: string;
  maxQty?: number;
  options?: ProductOption[];
}

export interface ProductOption {
  name: string;
  values: { label: string; priceCents: number }[];
}

export interface CardCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  templateCount: number;
}

export interface PhotoZone {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TextZone {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  maxChars?: number;
}

export interface CardTemplate {
  id: string;
  name: string;
  sku: string;
  type: 'photo' | 'non_photo';
  priceCents: number;
  previewUrl: string;
  overlayUrl?: string;
  thumbnailUrl: string;
  photoZone?: PhotoZone;
  textZones?: TextZone[];
  isPremium: boolean;
  isActive: boolean;
  category: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface PostcardConfig {
  size: string;
  basePriceCents: number;
  messageFeeCents: number;
  messageCharLimit: number;
  minQuantity: number;
  printSpecs: { dpi: number; bleedInches: number; fullBleed: boolean };
  shipping: {
    standardCents: number;
    premiumCents: number;
    freeThresholdCents: number;
    walletFreeShipping: boolean;
  };
  fonts: string[];
  returnAddressOptions: string[];
}

export interface PhotoSizeConfig {
  id: string;
  label: string;
  widthInches: number;
  heightInches: number;
  priceCents: number;
  popular?: boolean;
}

export interface PhotoFinishConfig {
  id: string;
  label: string;
  priceCents: number;
}

export interface PhotoConfig {
  sizes: PhotoSizeConfig[];
  finishes: PhotoFinishConfig[];
  printSpecs?: {
    dpi: number;
    bleedInches: number;
    outputFormat: string;
    qualityPercent: number;
  };
}

export interface DocumentPrintType {
  id: string;
  label: string;
  priceCents: number;
}

export interface DocumentConfig {
  printTypes: DocumentPrintType[];
  maxPages: number;
  paperSize: string;
  printSpecs?: {
    dpi: number;
    outputFormat: string;
  };
}

// ── Raw API shapes (backend is inconsistent on snake/camel casing) ──
// Kept internal to the module; callers always receive the normalised types.
interface RawCardTemplate {
  id: string;
  name?: string;
  templateName?: string;
  sku?: string;
  type?: string;
  priceCents?: number;
  price_cents?: number;
  basePriceCents?: number;
  previewUrl?: string;
  preview_url?: string;
  thumbnailUrl?: string;
  thumbnail_url?: string;
  overlayUrl?: string;
  overlay_url?: string;
  photoZone?: PhotoZone;
  photo_zone?: PhotoZone;
  textZones?: TextZone[];
  text_zones?: TextZone[];
  isPremium?: boolean;
  is_premium?: boolean;
  isActive?: boolean;
  is_active?: boolean;
  category?: { id: string; name: string; slug: string };
}

interface RawProduct {
  id: string;
  name: string;
  productType?: Product['type'];
  type?: Product['type'];
  description?: string;
  basePriceCents?: number;
  imageUrl?: string;
  maxQty?: number;
  options?: ProductOption[];
}

interface RawCardCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  sortOrder?: number;
  sort_order?: number;
  isActive?: boolean;
  is_active?: boolean;
  templateCount?: number;
  template_count?: number;
}

function normalizeCardTemplate(raw: RawCardTemplate): CardTemplate {
  return {
    id: raw.id,
    name: raw.name || raw.templateName || '',
    sku: raw.sku || '',
    type: raw.type === 'photo' ? 'photo' : 'non_photo',
    priceCents: raw.priceCents ?? raw.price_cents ?? raw.basePriceCents ?? 350,
    previewUrl: raw.previewUrl ?? raw.preview_url ?? raw.thumbnailUrl ?? '',
    overlayUrl: raw.overlayUrl ?? raw.overlay_url,
    thumbnailUrl: raw.thumbnailUrl ?? raw.thumbnail_url ?? raw.previewUrl ?? '',
    photoZone: raw.photoZone ?? raw.photo_zone,
    textZones: raw.textZones ?? raw.text_zones,
    isPremium: raw.isPremium ?? raw.is_premium ?? false,
    isActive: raw.isActive ?? raw.is_active ?? true,
    category: raw.category
      ? { id: raw.category.id, name: raw.category.name, slug: raw.category.slug }
      : { id: '', name: 'General', slug: 'general' },
  };
}

function normalizeProduct(raw: RawProduct): Product {
  return {
    id: raw.id,
    name: raw.name,
    type: (raw.productType || raw.type) as Product['type'],
    description: raw.description || '',
    basePriceCents: raw.basePriceCents || 0,
    imageUrl: raw.imageUrl,
    maxQty: raw.maxQty,
    options: raw.options,
  };
}

function normalizeCardCategory(c: RawCardCategory): CardCategory {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug,
    description: c.description,
    sortOrder: c.sortOrder ?? c.sort_order ?? 0,
    isActive: c.isActive ?? c.is_active ?? true,
    templateCount: c.templateCount ?? c.template_count ?? 0,
  };
}

export interface ProductsApi {
  getProducts(facilityId?: string): Promise<Product[]>;
  getProduct(id: string): Promise<Product>;
  getFacilityProducts(facilityId: string): Promise<Product[]>;
  getPostcardConfig(facilityId?: string): Promise<PostcardConfig>;
  getPhotoConfig(facilityId?: string): Promise<PhotoConfig>;
  getCardTemplates(facilityId?: string): Promise<CardTemplate[]>;
  getCardCategories(): Promise<CardCategory[]>;
  getCardTemplatesByCategory(categoryId: string): Promise<CardTemplate[]>;
  getCardTemplatesByType(type: 'photo' | 'non_photo'): Promise<CardTemplate[]>;
  getDocumentConfig(facilityId?: string): Promise<DocumentConfig>;
}

export function createProductsApi(client: ConsumerApiClient): ProductsApi {
  return {
    async getProducts(facilityId) {
      const query = facilityId ? `?facility_id=${facilityId}` : '';
      const data = await client.get<RawProduct[]>(`/products${query}`);
      return (data || []).map(normalizeProduct);
    },
    async getProduct(id) {
      const data = await client.get<RawProduct>(`/products/${id}`);
      return normalizeProduct(data);
    },
    async getFacilityProducts(facilityId) {
      const data = await client.get<RawProduct[]>(`/facilities/${facilityId}/products`);
      return (data || []).map(normalizeProduct);
    },
    getPostcardConfig(facilityId) {
      const query = facilityId ? `?facility_id=${facilityId}` : '';
      return client.get<PostcardConfig>(`/products/postcard-config${query}`);
    },
    getPhotoConfig(facilityId) {
      const query = facilityId ? `?facility_id=${facilityId}` : '';
      return client.get<PhotoConfig>(`/products/photo-config${query}`);
    },
    async getCardTemplates(facilityId) {
      const query = facilityId ? `?facility_id=${facilityId}` : '';
      const data = await client.get<RawCardTemplate[]>(`/card-templates${query}`);
      return (data || []).map(normalizeCardTemplate);
    },
    async getCardCategories() {
      const data = await client.get<RawCardCategory[]>('/card-categories');
      return (data || []).map(normalizeCardCategory);
    },
    async getCardTemplatesByCategory(categoryId) {
      const data = await client.get<RawCardTemplate[]>(`/card-templates?category=${categoryId}`);
      return (data || []).map(normalizeCardTemplate);
    },
    async getCardTemplatesByType(type) {
      const data = await client.get<RawCardTemplate[]>(`/card-templates?type=${type}`);
      return (data || []).map(normalizeCardTemplate);
    },
    getDocumentConfig(facilityId) {
      const query = facilityId ? `?facility_id=${facilityId}` : '';
      return client.get<DocumentConfig>(`/products/document-config${query}`);
    },
  };
}
